import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiMail, FiPaperclip, FiTrash2, FiSend, FiX, FiCheck, FiAlertCircle, FiUsers, FiUser, FiFileText, FiMapPin } from "react-icons/fi";
import AdminLayout from "../../layouts/AdminLayout";
import apiClient from "../../api/apiClient";
import { sendCustomEmail, getLocations } from "../../api/accountsService";
import useAuthStore from "../../store/authStore";

function SendMail() {
  const navigate = useNavigate();
  const isSuperUser = useAuthStore((state) => state.isSuperuser);

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sendToAll, setSendToAll] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);

  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch all users and locations on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, locRes] = await Promise.all([
          apiClient.get("/accounts/api/users/?all_for_chat=true"),
          getLocations().catch((err) => {
            console.error("Failed to load locations", err);
            return { data: { locations: [] } };
          }),
        ]);
        setUsers(usersRes.data?.users || []);
        setLocations(locRes.data?.locations || []);
      } catch (err) {
        console.error("Failed to load users or locations", err);
      }
    };
    fetchData();
  }, []);

  // Compute employee count per location
  const locationCounts = useMemo(() => {
    const counts = {};
    users.forEach((u) => {
      if (u.location) {
        counts[u.location] = (counts[u.location] || 0) + 1;
      }
    });
    return counts;
  }, [users]);

  // Total active employees in selected locations
  const targetedLocationUsersCount = useMemo(() => {
    if (selectedLocations.length === 0) return 0;
    return users.filter((u) => u.location && selectedLocations.includes(u.location)).length;
  }, [users, selectedLocations]);

  // Toggle a single location
  const handleToggleLocation = (code) => {
    setSelectedLocations((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  // Handle clicking outside user dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // User list filtering
  const filteredUsers = users.filter((user) => {
    // Exclude already selected
    if (selectedUsers.some((u) => u.employee_id === user.employee_id)) return false;

    const term = searchQuery.toLowerCase();
    return (
      user.full_name?.toLowerCase().includes(term) ||
      user.employee_id?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term)
    );
  });

  const handleSelectUser = (user) => {
    setSelectedUsers((prev) => [...prev, user]);
    setSearchQuery("");
    setIsDropdownOpen(false);
  };

  const handleRemoveUser = (userId) => {
    setSelectedUsers((prev) => prev.filter((u) => u.employee_id !== userId));
  };

  const handleRemoveRawEmail = (email) => {
    setSelectedUsers((prev) => prev.filter((u) => u.email !== email));
  };

  // Add raw email typed
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const val = searchQuery.trim().replace(/,$/, "");
      if (val && val.includes("@")) {
        // Check if already in selected
        if (!selectedUsers.some((u) => u.email.toLowerCase() === val.toLowerCase())) {
          const newRawRecipient = {
            employee_id: null,
            full_name: val,
            email: val,
            is_raw: true,
          };
          setSelectedUsers((prev) => [...prev, newRawRecipient]);
        }
        setSearchQuery("");
        setIsDropdownOpen(false);
      }
    }
  };

  // Attachments Handling
  const handleFileChange = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachments((prev) => [...prev, ...newFiles]);
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Drag and Drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      setAttachments((prev) => [...prev, ...droppedFiles]);
    }
  };

  // Send Email Action
  const handleSend = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");

    if (!subject.trim()) {
      setErrorMsg("Subject is required.");
      return;
    }
    if (!body.trim()) {
      setErrorMsg("Email body is required.");
      return;
    }
    if (!sendToAll && selectedUsers.length === 0 && selectedLocations.length === 0) {
      setErrorMsg("Please select at least one location, recipient, or choose 'Send to All'.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("subject", subject);
      formData.append("body", body);
      formData.append("send_to_all", sendToAll ? "true" : "false");

      if (!sendToAll) {
        if (selectedLocations.length > 0) {
          formData.append("locations", JSON.stringify(selectedLocations));
        }
        if (selectedUsers.length > 0) {
          // Collect resolved email addresses/IDs
          const toList = selectedUsers.map((u) => u.employee_id || u.email);
          formData.append("to_users", JSON.stringify(toList));
        }
      }

      attachments.forEach((file) => {
        formData.append("attachments", file);
      });

      const res = await sendCustomEmail(formData);

      setSuccessMsg(res.data.message || "Email sent successfully! ✉️");
      // Reset form
      setSubject("");
      setBody("");
      setSendToAll(false);
      setSelectedLocations([]);
      setSelectedUsers([]);
      setAttachments([]);
    } catch (err) {
      console.error(err);
      setErrorMsg(err.response?.data?.error || err.response?.data?.details || "Failed to send email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-2 sm:p-6">
        <div className="w-full max-w-[1100px] mx-auto">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl overflow-hidden text-white"
          >
            {/* Header banner */}
            <div className="bg-gradient-to-r from-indigo-600/50 to-blue-600/50 px-6 py-8 border-b border-white/15 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-5 sm:gap-6">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/10 flex items-center justify-center shadow-lg border border-white/20 backdrop-blur-md flex-shrink-0">
                  <FiMail className="text-3xl text-indigo-200" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Send a Mail</h1>
                  <p className="text-indigo-200/80 text-xs sm:text-sm mt-1">
                    Compose and dispatch custom emails to users and external addresses.
                  </p>
                </div>
              </div>
              {isSuperUser && (
                <button
                  type="button"
                  onClick={() => navigate("/admin/mail-tracking")}
                  className="px-5 py-2.5 rounded-xl bg-white/15 hover:bg-white/25 border border-white/20 text-white font-semibold text-sm transition-all duration-200 shadow-md flex items-center gap-2 self-start sm:self-center active:scale-95"
                >
                  📊 Track Emails
                </button>
              )}
            </div>

            <form onSubmit={handleSend} className="p-6 sm:p-8 space-y-6">
              {/* Recipients Row */}
              <div className="space-y-3 relative">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <label className="text-sm font-semibold tracking-wide flex items-center gap-2">
                    <FiUsers className="text-indigo-300" /> Recipients
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer select-none text-xs sm:text-sm text-indigo-200 hover:text-white transition-all">
                    <input
                      type="checkbox"
                      checked={sendToAll}
                      onChange={(e) => {
                        setSendToAll(e.target.checked);
                        if (e.target.checked) {
                          setSelectedUsers([]);
                          setSelectedLocations([]);
                        }
                      }}
                      className="w-4 h-4 rounded border-white/30 bg-white/5 text-indigo-500 focus:ring-0 focus:ring-offset-0 cursor-pointer"
                    />
                    Send to All Active Users (All Locations)
                  </label>
                </div>

                {!sendToAll ? (
                  <div className="space-y-3">
                    {/* Location Selection Filter (Single or Multiple) */}
                    {locations.length > 0 && (
                      <div className="bg-white/5 border border-white/10 rounded-2xl p-3.5 space-y-2.5">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-semibold text-indigo-200 flex items-center gap-1.5">
                            <FiMapPin className="text-indigo-400" /> Filter by Location (Single or Multiple)
                          </span>
                          {selectedLocations.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setSelectedLocations([])}
                              className="text-[11px] text-indigo-300 hover:text-white transition-colors underline"
                            >
                              Clear locations
                            </button>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {locations.map((loc) => {
                            const isSelected = selectedLocations.includes(loc.code);
                            const count = locationCounts[loc.code] || 0;
                            return (
                              <button
                                key={loc.code}
                                type="button"
                                onClick={() => handleToggleLocation(loc.code)}
                                className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 border ${
                                  isSelected
                                    ? "bg-indigo-600 text-white border-indigo-400 shadow-lg shadow-indigo-600/30 scale-102"
                                    : "bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border-white/15"
                                }`}
                              >
                                <FiMapPin className={isSelected ? "text-white" : "text-indigo-300"} />
                                <span>{loc.name || loc.code}</span>
                                <span
                                  className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                                    isSelected ? "bg-white/20 text-white" : "bg-white/10 text-white/60"
                                  }`}
                                >
                                  {count}
                                </span>
                                {isSelected && <FiCheck className="text-xs ml-0.5" />}
                              </button>
                            );
                          })}
                        </div>

                        {selectedLocations.length > 0 && (
                          <div className="text-[11px] sm:text-xs text-indigo-200/90 bg-indigo-500/10 border border-indigo-400/20 rounded-xl px-3 py-1.5 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                            <span>
                              Targeting <strong>{targetedLocationUsersCount} active employee{targetedLocationUsersCount === 1 ? "" : "s"}</strong> across {selectedLocations.length} location{selectedLocations.length === 1 ? "" : "s"} ({selectedLocations
                                .map((c) => locations.find((l) => l.code === c)?.name || c)
                                .join(", ")})
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Specific recipients / external emails */}
                    <div className="space-y-1.5">
                      <label className="text-xs text-white/70 flex items-center gap-1.5">
                        <FiUser className="text-indigo-300" />
                        {selectedLocations.length > 0
                          ? "Additional individual recipients or external emails (Optional):"
                          : "Specific individual recipients or external emails:"}
                      </label>

                      <div
                        ref={dropdownRef}
                        className="w-full bg-white/5 border border-white/20 rounded-2xl p-2.5 transition-all focus-within:border-indigo-400 focus-within:bg-white/10 relative"
                      >
                        <div className="flex flex-wrap gap-2 items-center">
                          {/* Selected tags */}
                          {selectedUsers.map((user) => (
                            <div
                              key={user.employee_id || user.email}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-500/30 border border-indigo-400/40 text-xs sm:text-sm font-medium hover:bg-indigo-500/40 transition-all"
                            >
                              {user.photo ? (
                                <img
                                  src={user.photo.startsWith("http") ? user.photo : `/media/${user.photo}`}
                                  alt={user.full_name}
                                  className="w-5 h-5 rounded-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <div className="w-5 h-5 rounded-full bg-indigo-400/30 flex items-center justify-center text-[10px]">
                                  <FiUser className="text-[10px]" />
                                </div>
                              )}
                              <span className="truncate max-w-[120px]">{user.full_name}</span>
                              <button
                                type="button"
                                onClick={() =>
                                  user.is_raw ? handleRemoveRawEmail(user.email) : handleRemoveUser(user.employee_id)
                                }
                                className="text-white/60 hover:text-white hover:bg-white/10 rounded-full p-0.5"
                              >
                                <FiX className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}

                          {/* Input Search Field */}
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => {
                              setSearchQuery(e.target.value);
                              setIsDropdownOpen(true);
                            }}
                            onFocus={() => setIsDropdownOpen(true)}
                            onKeyDown={handleKeyDown}
                            placeholder={
                              selectedUsers.length === 0
                                ? selectedLocations.length > 0
                                  ? "Type additional employee name or external email..."
                                  : "Search by Name, Employee ID or type email & hit Enter..."
                                : "Add more..."
                            }
                            className="flex-1 min-w-[200px] bg-transparent border-0 outline-none p-1 text-sm placeholder-white/45 focus:ring-0"
                          />
                        </div>

                        {/* Dropdown Suggestions */}
                        <AnimatePresence>
                          {isDropdownOpen && (searchQuery.trim().length > 0 || filteredUsers.length > 0) && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="absolute left-0 right-0 mt-3 z-50 max-h-60 overflow-y-auto rounded-2xl bg-slate-900/95 border border-white/20 shadow-2xl scrollbar-thin scrollbar-thumb-white/25 scrollbar-track-transparent"
                            >
                              {/* Typed external email preview option */}
                              {searchQuery.trim().includes("@") && (
                                <div
                                  onClick={() => {
                                    const val = searchQuery.trim();
                                    if (!selectedUsers.some((u) => u.email.toLowerCase() === val.toLowerCase())) {
                                      setSelectedUsers((prev) => [
                                        ...prev,
                                        { employee_id: null, full_name: val, email: val, is_raw: true },
                                      ]);
                                      setSearchQuery("");
                                      setIsDropdownOpen(false);
                                    }
                                  }}
                                  className="px-4 py-3 hover:bg-indigo-600/30 cursor-pointer flex items-center gap-3 border-b border-white/10 text-sm font-semibold text-indigo-200"
                                >
                                  <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center">
                                    <FiMail className="w-4 h-4 text-indigo-300" />
                                  </div>
                                  <div>
                                    <p className="text-white font-medium">Add raw email address</p>
                                    <p className="text-xs text-white/50">{searchQuery.trim()}</p>
                                  </div>
                                </div>
                              )}

                              {filteredUsers.length === 0 ? (
                                searchQuery.trim() && !searchQuery.trim().includes("@") ? (
                                  <div className="px-4 py-4 text-center text-white/40 text-sm">
                                    No matching active users found.
                                  </div>
                                ) : null
                              ) : (
                                filteredUsers.map((user) => (
                                  <div
                                    key={user.employee_id}
                                    onClick={() => handleSelectUser(user)}
                                    className="px-4 py-3 hover:bg-indigo-600/30 cursor-pointer flex items-center gap-3 transition-all text-left"
                                  >
                                    {user.photo ? (
                                      <img
                                        src={user.photo.startsWith("http") ? user.photo : `/media/${user.photo}`}
                                        alt={user.full_name}
                                        className="w-8 h-8 rounded-full object-cover border border-white/20"
                                        onError={(e) => {
                                          e.target.src = "/images/avatar.png";
                                        }}
                                      />
                                    ) : (
                                      <div className="w-8 h-8 rounded-full bg-indigo-500/20 border border-white/10 flex items-center justify-center text-xs text-indigo-300">
                                        <FiUser />
                                      </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-2">
                                        <p className="text-sm font-semibold text-white truncate">{user.full_name}</p>
                                        {user.location && (
                                          <span className="text-[10px] bg-white/10 border border-white/10 px-1.5 py-0.2 rounded text-indigo-300">
                                            {user.location}
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs text-white/50 truncate">
                                        {user.employee_id} &bull; {user.email}
                                      </p>
                                    </div>
                                  </div>
                                ))
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="w-full bg-indigo-600/20 border border-indigo-500/35 rounded-2xl p-4 flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center text-xl">
                      📢
                    </div>
                    <div>
                      <p className="font-bold text-indigo-200">Sending to ALL active users across all locations</p>
                      <p className="text-xs text-indigo-300/80">
                        This email will be delivered to every active employee, manager, and administrator on record.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Subject Row */}
              <div className="space-y-2">
                <label className="text-sm font-semibold tracking-wide block text-left">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject line..."
                  required
                  className="w-full bg-white/5 border border-white/20 rounded-2xl px-4 py-3.5 text-white text-sm outline-none focus:border-indigo-400 focus:bg-white/10 transition-all placeholder-white/35"
                />
              </div>

              {/* Body Row */}
              <div className="space-y-2">
                <label className="text-sm font-semibold tracking-wide block text-left">Email Body</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Compose your email message here..."
                  rows={9}
                  required
                  className="w-full bg-white/5 border border-white/20 rounded-2xl px-4 py-4 text-white text-sm outline-none focus:border-indigo-400 focus:bg-white/10 transition-all placeholder-white/35 resize-y font-sans leading-relaxed"
                />
              </div>

              {/* Attachments Row */}
              <div className="space-y-2">
                <label className="text-sm font-semibold tracking-wide block text-left">Attachments</label>
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-2xl p-6 sm:p-8 text-center cursor-pointer transition-all ${
                    isDragOver
                      ? "border-indigo-400 bg-indigo-500/10 scale-[0.99]"
                      : "border-white/20 bg-white/5 hover:bg-white/10 hover:border-white/30"
                  }`}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    multiple
                    className="hidden"
                  />
                  <FiPaperclip className="mx-auto text-3xl mb-3 text-indigo-300" />
                  <p className="text-sm font-semibold">Drag & drop files here, or click to browse</p>
                  <p className="text-xs text-white/45 mt-1.5">Attach documents, images, media, or archives</p>
                </div>

                {/* Attachments List */}
                <AnimatePresence>
                  {attachments.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4"
                    >
                      {attachments.map((file, idx) => {
                        const isImage = file.type.startsWith("image/");
                        return (
                          <motion.div
                            key={`${file.name}-${idx}`}
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="flex items-center justify-between p-3.5 bg-white/5 border border-white/10 rounded-2xl gap-3 hover:bg-white/10 transition-all"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              {isImage ? (
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt="preview"
                                  className="w-10 h-10 object-cover rounded-lg border border-white/10 flex-shrink-0"
                                  onLoad={(e) => URL.revokeObjectURL(e.target.src)}
                                />
                              ) : (
                                <div className="w-10 h-10 bg-indigo-500/20 border border-white/10 flex items-center justify-center rounded-lg text-indigo-300 flex-shrink-0">
                                  <FiFileText className="text-xl" />
                                </div>
                              )}
                              <div className="min-w-0 text-left">
                                <p className="text-xs sm:text-sm font-medium truncate text-white">{file.name}</p>
                                <p className="text-[10px] sm:text-xs text-white/50">{formatFileSize(file.size)}</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveAttachment(idx)}
                              className="w-8 h-8 rounded-full bg-red-500/10 hover:bg-red-500/25 border border-red-500/30 flex items-center justify-center text-red-400 hover:text-red-300 transition-all flex-shrink-0 active:scale-95"
                              title="Remove file"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </motion.div>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Banners */}
              {successMsg && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-emerald-500/20 border border-emerald-400/40 rounded-2xl px-5 py-4 flex items-start gap-3 text-left"
                >
                  <FiCheck className="text-emerald-400 text-xl flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-emerald-300 text-sm font-semibold">Success</p>
                    <p className="text-emerald-200/90 text-xs sm:text-sm mt-0.5">{successMsg}</p>
                  </div>
                </motion.div>
              )}

              {errorMsg && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-red-500/20 border border-red-400/40 rounded-2xl px-5 py-4 flex items-start gap-3 text-left"
                >
                  <FiAlertCircle className="text-red-400 text-xl flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-300 text-sm font-semibold">Error</p>
                    <p className="text-red-200/90 text-xs sm:text-sm mt-0.5">{errorMsg}</p>
                  </div>
                </motion.div>
              )}

              {/* Submit Buttons */}
              <div className="flex justify-end gap-3 pt-3 border-t border-white/10">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-8 py-3.5 rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500 hover:from-indigo-600 hover:via-purple-600 hover:to-blue-600 text-white font-bold text-sm sm:text-base shadow-lg hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <FiSend />
                      <span>Send Email</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default SendMail;
